/**
 * Finance chart types, added for Vudget.
 *
 * The original @vitness/charts components are hand-rolled single-series SVG.
 * These need stacked areas, dual axes and floating bars, so they're Chart.js
 * backed — but keep the same prop conventions (data / labels / height /
 * formatValue) so the package still reads as one thing.
 */
import { useEffect, useRef } from 'react';
import { Chart } from 'chart.js';
import {
  registerCharts, baseOptions, chartColours, compactMoney, PALETTE, MONO,
  chartMaskStamp,
} from './chartTheme.js';

export interface Series {
  label: string;
  data: number[];
  color?: string;
}

interface Common {
  labels: string[];
  height?: number;
  formatValue?: (v: number) => string;
  /** Hide the legend when a caption already explains the series. */
  legend?: boolean;
}

/** Mount a Chart.js instance and keep it in step with props. */
function useChart(config: () => any, deps: unknown[]) {
  const ref = useRef<HTMLCanvasElement>(null);
  const chart = useRef<Chart | null>(null);

  useEffect(() => {
    registerCharts();
    if (!ref.current) return;
    chart.current?.destroy();
    chart.current = new Chart(ref.current, config());
    return () => { chart.current?.destroy(); chart.current = null; };
    // chartMaskStamp: painted figures can only follow the privacy mask by
    // being repainted, so a mask toggle rebuilds every chart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, chartMaskStamp()]);

  return ref;
}

const wrap = (h: number) => ({ position: 'relative' as const, height: h, minWidth: 0 });

// ───────────────────────────── Waterfall ─────────────────────────────

export interface WaterfallStep { label: string; value: number }

/**
 * Floating-bar waterfall: a baseline, one bar per driver, then the result.
 * This is the chart that explains *why* a projection lands where it does.
 */
export function WaterfallChart({
  baseline, steps, result, height = 240, formatValue = compactMoney,
}: {
  baseline: number;
  steps: WaterfallStep[];
  result: number;
  height?: number;
  formatValue?: (v: number) => string;
}) {
  const ref = useChart(() => {
    const c = chartColours();
    const labels = ['Baseline', ...steps.map(s => s.label), 'Result'];
    const bars: [number, number][] = [];
    const colors: string[] = [];

    bars.push([0, baseline]);
    colors.push(baseline >= 0 ? c.success : c.danger);

    let running = baseline;
    for (const s of steps) {
      bars.push([running, running + s.value]);
      colors.push(s.value >= 0 ? c.success + 'cc' : c.danger + 'cc');
      running += s.value;
    }

    bars.push([0, result]);
    colors.push(result >= 0 ? c.success : c.danger);

    const opts = baseOptions(formatValue);
    return {
      type: 'bar',
      data: { labels, datasets: [{ label: 'Impact', data: bars, backgroundColor: colors, borderWidth: 0, borderSkipped: false }] },
      options: {
        ...opts,
        indexAxis: 'y' as const,
        plugins: { ...opts.plugins, legend: { display: false } },
        scales: {
          x: opts.scales.y,
          y: { grid: { display: false }, border: { display: false },
               ticks: { color: c.text, font: { family: MONO, size: 10 } } },
        },
      },
    };
  }, [baseline, JSON.stringify(steps), result, height]);

  return <div style={wrap(height)}><canvas ref={ref} /></div>;
}

// ───────────────────────────── Combo ─────────────────────────────

/** Bars on the left axis, a line on the right. Surplus + cash buffer. */
export function ComboChart({
  labels, bars, line, height = 250, formatValue = compactMoney, barColorFor, onIndex,
}: Common & {
  bars: Series;
  line: Series;
  barColorFor?: (v: number) => string;
  /**
   * Fires with the hovered/tapped data index (index-mode interaction, so
   * anywhere in the column counts). This is what makes the chart usable on
   * touch: the caller renders a persistent readout of the selected month,
   * because a hover tooltip under a finger is unreadable by definition.
   */
  onIndex?: (i: number) => void;
}) {
  // Ref'd so a new callback identity doesn't rebuild the whole chart.
  const onIndexRef = useRef(onIndex);
  onIndexRef.current = onIndex;

  const ref = useChart(() => {
    const c = chartColours();
    const opts = baseOptions(formatValue);
    return {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: bars.label, data: bars.data, yAxisID: 'y', order: 2, borderWidth: 0,
            backgroundColor: bars.data.map(v =>
              barColorFor ? barColorFor(v) : (v >= 0 ? c.success + 'bf' : c.danger + 'cc')),
          },
          {
            type: 'line' as const, label: line.label, data: line.data, yAxisID: 'y1', order: 1,
            borderColor: line.color ?? c.warning, borderWidth: 2, pointRadius: 0, tension: 0.25,
          },
        ],
      },
      options: {
        ...opts,
        scales: {
          x: opts.scales.x,
          y: { ...opts.scales.y, position: 'left' as const },
          y1: { ...opts.scales.y, position: 'right' as const, grid: { drawOnChartArea: false } },
        },
        onHover: (_e: unknown, els: { index: number }[]) => {
          if (els.length) onIndexRef.current?.(els[0].index);
        },
        onClick: (_e: unknown, els: { index: number }[]) => {
          if (els.length) onIndexRef.current?.(els[0].index);
        },
      },
    };
  }, [JSON.stringify(labels), JSON.stringify(bars), JSON.stringify(line), height]);

  return <div style={wrap(height)}><canvas ref={ref} /></div>;
}

// ───────────────────────── Stacked area ─────────────────────────

/** Stacked outflow bands with an optional overlay line for income. */
export function StackedAreaChart({
  labels, series, overlay, height = 250, formatValue = compactMoney, onIndex,
}: Common & { series: Series[]; overlay?: Series; onIndex?: (i: number) => void }) {
  const onIndexRef = useRef(onIndex);
  onIndexRef.current = onIndex;
  const ref = useChart(() => {
    const c = chartColours();
    const opts = baseOptions(formatValue);
    const datasets: any[] = series.map((s, i) => {
      const col = s.color ?? PALETTE[i % PALETTE.length];
      return {
        label: s.label, data: s.data, fill: true, stack: 'out',
        backgroundColor: col + 'd9', borderColor: col, borderWidth: 0,
        pointRadius: 0, tension: 0.2,
      };
    });
    if (overlay) {
      datasets.push({
        label: overlay.label, data: overlay.data, fill: false,
        borderColor: overlay.color ?? c.text, borderWidth: 2,
        borderDash: [5, 4], pointRadius: 0, tension: 0.2,
      });
    }
    return {
      type: 'line',
      data: { labels, datasets },
      options: {
        ...opts,
        plugins: {
          ...opts.plugins,
          legend: { ...opts.plugins.legend, labels: { ...opts.plugins.legend.labels, font: { family: MONO, size: 9 }, boxWidth: 8 } },
        },
        scales: { x: opts.scales.x, y: { ...opts.scales.y, stacked: true } },
        onHover: (_e: unknown, els: { index: number }[]) => {
          if (els.length) onIndexRef.current?.(els[0].index);
        },
        onClick: (_e: unknown, els: { index: number }[]) => {
          if (els.length) onIndexRef.current?.(els[0].index);
        },
      },
    };
  }, [JSON.stringify(labels), JSON.stringify(series), JSON.stringify(overlay), height]);

  return <div style={wrap(height)}><canvas ref={ref} /></div>;
}

// ───────────────────────── Area (+ comparison) ─────────────────────────

/** Filled line with an optional dashed comparison — projected vs baseline. */
export function AreaChart({
  labels, series, compare, height = 220, formatValue = compactMoney, stacked = false, onIndex,
}: Common & { series: Series[]; compare?: Series; stacked?: boolean; onIndex?: (i: number) => void }) {
  const onIndexRef = useRef(onIndex);
  onIndexRef.current = onIndex;
  const ref = useChart(() => {
    const c = chartColours();
    const opts = baseOptions(formatValue);
    const datasets: any[] = series.map((s, i) => {
      const col = s.color ?? (i === 0 ? c.success : PALETTE[i % PALETTE.length]);
      return {
        label: s.label, data: s.data, fill: true,
        borderColor: col, borderWidth: 2,
        backgroundColor: col + (stacked ? 'cc' : '22'),
        pointRadius: 0, tension: 0.25,
        ...(stacked ? { stack: 'a' } : {}),
      };
    });
    if (compare) {
      datasets.push({
        label: compare.label, data: compare.data, fill: false,
        borderColor: compare.color ?? c.muted, borderWidth: 1.5,
        borderDash: [4, 4], pointRadius: 0, tension: 0.25,
      });
    }
    return {
      type: 'line',
      data: { labels, datasets },
      options: {
        ...opts,
        scales: { x: opts.scales.x, y: { ...opts.scales.y, stacked } },
        onHover: (_e: unknown, els: { index: number }[]) => {
          if (els.length) onIndexRef.current?.(els[0].index);
        },
        onClick: (_e: unknown, els: { index: number }[]) => {
          if (els.length) onIndexRef.current?.(els[0].index);
        },
      },
    };
  }, [JSON.stringify(labels), JSON.stringify(series), JSON.stringify(compare), height, stacked]);

  return <div style={wrap(height)}><canvas ref={ref} /></div>;
}

// ───────────────────────────── Donut ─────────────────────────────

export function DonutChart({
  labels, data, height = 250, formatValue = compactMoney, colors, legend = true,
}: {
  labels: string[]; data: number[]; height?: number;
  formatValue?: (v: number) => string; colors?: string[];
  /**
   * Chart.js draws its legend inside the canvas, which means it eats the space
   * the ring could be using. Turn it off when the caller renders its own —
   * a real list can carry values and sort order, which the built-in can't.
   */
  legend?: boolean;
}) {
  const ref = useChart(() => {
    const c = chartColours();
    const opts = baseOptions(formatValue);
    return {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: colors ?? labels.map((_, i) => PALETTE[i % PALETTE.length]),
          borderColor: c.card, borderWidth: 2,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: '62%',
        plugins: {
          legend: legend
            ? { ...opts.plugins.legend, labels: { ...opts.plugins.legend.labels, font: { family: MONO, size: 9 }, boxWidth: 8, padding: 7 } }
            : { display: false },
          tooltip: {
            ...opts.plugins.tooltip,
            callbacks: { label: (ctx: any) => `${ctx.label}: ${formatValue(ctx.parsed)}` },
          },
        },
      },
    };
  }, [JSON.stringify(labels), JSON.stringify(data), height, legend]);

  return <div style={wrap(height)}><canvas ref={ref} /></div>;
}

// ───────────────────────── ProgressBar (pure CSS) ─────────────────────────

export function ProgressBar({
  pct, label, caption, height = 8, colorFor,
}: {
  pct: number; label?: string; caption?: string; height?: number;
  colorFor?: (p: number) => string;
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  const colour = colorFor
    ? colorFor(clamped)
    : clamped >= 100 ? 'var(--vn-success)'
    : clamped >= 50 ? 'var(--vn-warning)'
    : 'var(--vn-danger)';

  return (
    <div>
      {(label || caption) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
          {label && <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>}
          {caption && (
            <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--vn-text-muted)', whiteSpace: 'nowrap' }}>
              {caption}
            </span>
          )}
        </div>
      )}
      <div style={{
        height, borderRadius: height / 2, overflow: 'hidden',
        background: 'var(--vn-surface-inset)', border: '1px solid var(--vn-border-default)',
      }}>
        <div style={{ width: `${clamped}%`, height: '100%', background: colour, transition: 'width .4s ease' }} />
      </div>
    </div>
  );
}
